import { ChevronDownIcon } from 'lucide-react'
import { useState } from 'react'

import { Popover, StepNav } from 'slab'

import { selectedPlansFromSmartBudget } from '../navigationUtils'
import { useOnboarding } from '../OnboardingContext'
import { canShowThisStep, isThisStepCompleted } from '../OnboardingSteps'
import { type OnboardingInfo, type Step } from '../types'
import { isNewQleFlow, isRenewal, shouldSkipQleDocumentUpload } from '../utils'

const isAvailable = (
  currentStep: Step | null,
  step: Step,
  onboardingData?: OnboardingInfo | null,
  isHawaiiState?: boolean,
  featureFlags?: { [key: string]: boolean }
): boolean => {
  // If user is from Hawaii, only allow the generate_allowances step
  if (isHawaiiState && step !== 'generate_allowances') {
    return false
  }

  return (
    !!onboardingData &&
    currentStep !== null &&
    canShowThisStep({ data: onboardingData, step, featureFlags })
  )
}

const isCompleted = (
  currentStep: Step | null,
  step: Step,
  onboardingData?: OnboardingInfo | null,
  isHawaiiState?: boolean,
  featureFlags?: { [key: string]: boolean }
): boolean => {
  // If user is from Hawaii, only allow the generate_allowances step
  if (isHawaiiState && step !== 'generate_allowances') {
    return false
  }

  if (currentStep === 'renewal_confirm_info' && step === 'renewal_show_summary') {
    // If there is at least one plan selected, mark renewal_show_summary as completed
    if (onboardingData?.enrollments?.length && onboardingData?.enrollments?.length > 0) {
      return true
    }
    // Otherwise, we mark it as false
    return false
  }

  return (
    !!onboardingData &&
    currentStep !== null &&
    // This function would fall back to canShowThisStep if the step doesn't have isCompleted defined
    !!isThisStepCompleted({ data: onboardingData, step, featureFlags }) &&
    // We don't mark current step as completed to distinguish from current step with other steps
    currentStep !== step
  )
}

export interface StepConfig {
  stepName?: Step;
  sectionStepNames?: Step[]; // For steps with multiple active keys (e.g., "Your insurance")
  stepLabel: string;
  alternativeActiveSteps?: Step[];
  subSteps?: {
    stepName: Step;
    stepLabel: string;
    alternativeActiveSteps?: Step[];
  }[];
}

const getPlansStepInfo = (
  data: OnboardingInfo | null | undefined,
  currentStep?: Step | null,
  enrollmentEventId?: string
): StepConfig => {
  const alternativeActiveSteps: Step[] = [
    'medical_plan_details',
    'medical_plan_comparison',
    'smart_budget',
  ]

  if (!data || !data?.allowance) {
    return {
      stepName: 'medical_plan',
      stepLabel: 'Your insurance',
      alternativeActiveSteps,
    }
  }

  const substeps: StepConfig['subSteps'] = [
    {
      stepName: 'medical_plan',
      stepLabel: 'Medical',
      alternativeActiveSteps,
    },
  ]
  const sectionStepNames: Step[] = ['medical_plan']
  if (!data.restrictions?.dental_not_available) {
    substeps.push({ stepName: 'dental_plan', stepLabel: 'Dental' })
    sectionStepNames.push('dental_plan')
  }
  if (!data.restrictions?.vision_not_available) {
    substeps.push({ stepName: 'vision_plan', stepLabel: 'Vision' })
    sectionStepNames.push('vision_plan')
  }

  // Check if user came from smart_budget by looking backwards through navigation history
  // Pass currentStep to handle timing issue where Navigation renders before history is updated
  const userCameFromSmartBudget = selectedPlansFromSmartBudget(enrollmentEventId, currentStep)

  // If on smart_budget page OR if user came from smart_budget, show "Your insurance" without sub-navigation
  if (currentStep === 'smart_budget' || (userCameFromSmartBudget && currentStep !== 'medical_plan')) {
    return {
      stepName: 'smart_budget',
      stepLabel: 'Your insurance',
    }
  }

  if (substeps.length === 1) {
    return {
      stepName: 'medical_plan',
      stepLabel: 'Your insurance',
      alternativeActiveSteps,
    }
  }

  return {
    stepName: 'medical_plan',
    stepLabel: 'Your insurance',
    sectionStepNames,
    subSteps: substeps as Array<{ stepName: Step; stepLabel: string }>,
  }
}

const getEnrollmentStepInfo = (
  data: OnboardingInfo | null | undefined
): StepConfig | null => {
  if (data?.state_config) {
    const longName = {
      CO: 'Colorado',
      NV: 'Nevada',
      GA: 'Georgia',
      NJ: 'New Jersey',
    }[data.state_config]
    return {
      stepName: 'state_form',
      stepLabel: `${longName} form`,
    }
  }

  return null
}

const maybeAddAboutYouPage = (data: OnboardingInfo | null | undefined): StepConfig | null => {
  if ((data?.missing_fields?.allowance || []).length === 0) {
    return null
  }
  return {
    stepName: 'generate_allowances',
    stepLabel: 'About you',
  }
}

const maybeAddQLEDocumentUpload = (data: OnboardingInfo | null | undefined): StepConfig | null => {
  if (shouldSkipQleDocumentUpload(data?.enrollment_event)) {
    return null
  }
  return {
    stepName: 'qle_document_upload',
    stepLabel: 'Document upload',
  }
}

const getAdditionalDetailsStepInfo = (
  data: OnboardingInfo | null | undefined
): StepConfig | null => {
  if (
    data?.restrictions?.medicare_eligible &&
    data.restrictions?.medicare_eligible?.length > 0
  ) {
    return null
  }

  return {
    stepName: 'additional_details',
    stepLabel: 'Additional details',
  }
}

export const getStepsFromData = (
  data: OnboardingInfo | null | undefined,
  currentStep?: Step | null,
  enrollmentEventId?: string,
  newQleEnabled?: boolean | null
): Array<StepConfig | null> => {
  if (data && isRenewal(data)) {
    return [
      { stepName: 'renewal_confirm_info', stepLabel: 'Personal details' },
      { stepName: 'renewal_show_summary', stepLabel: 'Budget and plan changes' },
      getPlansStepInfo(data, currentStep, enrollmentEventId),
      getEnrollmentStepInfo(data),
      { stepName: 'renewal_confirm_enrollment', stepLabel: 'Confirm and enroll' },
    ]
  }
  if (data && isNewQleFlow(data, newQleEnabled)) {
    return [
      { stepName: 'qle_details', stepLabel: 'Qualifying life event details' },
      maybeAddAboutYouPage(data),
      { stepName: 'renewal_confirm_info', stepLabel: 'Dependent changes' },
      { stepName: 'renewal_show_summary', stepLabel: 'Budget and plan changes' },
      getPlansStepInfo(data, currentStep, enrollmentEventId),
      getEnrollmentStepInfo(data),
      maybeAddQLEDocumentUpload(data),
      { stepName: 'renewal_confirm_enrollment', stepLabel: 'Confirm and enroll' },
    ]
  }

  return [
    {
      stepName: 'generate_allowances',
      stepLabel: 'About you',
      alternativeActiveSteps: ['phone_verification'],
    },
    { stepName: 'view_allowances', stepLabel: 'Your budget' },
    getAdditionalDetailsStepInfo(data),
    getPlansStepInfo(data, currentStep, enrollmentEventId),
    getEnrollmentStepInfo(data),
    { stepName: 'finalize_enrollment', stepLabel: 'Confirm and enroll' },
  ]
}

const getStepNumbers = (steps: Array<StepConfig> = [], step: Step | null) => {
  const currentStep = (step || '').split(':')[0] as Step
  const flattenedSteps = steps.flatMap<StepConfig>(
    (step) => step?.subSteps ?? [step]
  )
  const currentStepIndex = flattenedSteps.findIndex((step) => {
    return (
      step?.stepName === currentStep ||
      step?.alternativeActiveSteps?.includes(currentStep!)
    )
  })

  return {
    currentStepNumber: currentStepIndex + 1,
    totalStepsNumber: flattenedSteps.length,
  }
}

export interface NavigationProps {
  currentStep: Step | null;
  displayInDropdown?: boolean;
  onClickStep: (step: Step) => void;
}

export function Navigation ({
  currentStep,
  displayInDropdown,
  onClickStep,
}: NavigationProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const { onboardingData, isHawaiiState, enrollmentEventId, showSmartBudget, newQleEnabled } = useOnboarding()

  const featureFlags = {
    showSmartBudget: showSmartBudget ?? false,
    newQleEnabled: newQleEnabled ?? false,
  }

  const steps = getStepsFromData(onboardingData, currentStep, enrollmentEventId, newQleEnabled).filter(
    Boolean
  ) as Array<StepConfig>

  function renderSteps () {
    return (
      <StepNav data-testid='step-nav'>
        {steps.map(
          ({
            stepName,
            sectionStepNames,
            stepLabel,
            alternativeActiveSteps,
            subSteps,
          }) =>
            subSteps
              ? (
                <StepNav.Step
                  key={stepLabel}
                  as='button'
                  active={
                  sectionStepNames?.some((k) => currentStep === k) || false
                }
                  complete={
                  sectionStepNames?.every((k) =>
                    isCompleted(currentStep, k, onboardingData, isHawaiiState, featureFlags)
                  ) || false
                }
                  name={stepLabel}
                >
                  {subSteps.map(({ stepName, stepLabel }) => (
                    <StepNav.SubStep
                      key={stepName}
                      as='button'
                      active={currentStep === stepName}
                      available={isAvailable(
                        currentStep,
                        stepName,
                        onboardingData,
                        isHawaiiState,
                        featureFlags
                      )}
                      complete={isCompleted(
                        currentStep,
                        stepName,
                        onboardingData,
                        isHawaiiState,
                        featureFlags
                      )}
                      name={stepLabel}
                      onClick={() => {
                        onClickStep(stepName)

                        if (dropdownOpen) {
                          setDropdownOpen(false)
                        }
                      }}
                    />
                  ))}
                </StepNav.Step>
                )
              : (
                <StepNav.Step
                  key={stepName as string}
                  as='button'
                  active={
                  currentStep === stepName ||
                  alternativeActiveSteps?.includes(currentStep!)
                }
                  available={isAvailable(
                    currentStep,
                    stepName!,
                    onboardingData,
                    isHawaiiState,
                    featureFlags
                  )}
                  complete={isCompleted(
                    currentStep,
                    stepName!,
                    onboardingData,
                    isHawaiiState,
                    featureFlags
                  )}
                  name={stepLabel}
                  onClick={() => {
                    onClickStep(stepName!)

                    if (dropdownOpen) {
                      setDropdownOpen(false)
                    }
                  }}
                />
                )
        )}
      </StepNav>
    )
  }

  if (displayInDropdown) {
    const { currentStepNumber, totalStepsNumber } = getStepNumbers(
      steps,
      currentStep
    )

    return (
      <Popover
        align='end'
        side='bottom'
        trigger={
          <button
            className='group/trigger body data-[state*="open"]:text-gray-800 transition'
            type='button'
          >
            <div className='flex items-center gap-2'>
              <span>
                Step {currentStepNumber} / {totalStepsNumber}
              </span>
              <span className='text-gray-500 group-data-[state*="open"]/trigger:rotate-180'>
                <ChevronDownIcon size={16} />
              </span>
            </div>
          </button>
        }
      >
        <div className='p-4'>
          {renderSteps()}
        </div>
      </Popover>
    )
  }

  return (
    <>
      {renderSteps()}
    </>
  )
}
